/**
 * Picsart CDN image transforms, ported from genai-studio-v2
 * (`src/utils/media/optimizeImageUrl.ts` + `LightboxMedia/getLightboxImageSize`).
 *
 * Drive assets are served at full generation resolution — a grid of them is
 * megabytes of pixels nobody sees at 170px. The CDN resizes and re-encodes from
 * query params, so the fix is entirely in the URL: `type=webp&to=min&r=<size>`.
 *
 * These URLs stay `unoptimized` for `next/image`: the drive hosts aren't in
 * `next.config.mjs` `remotePatterns`, and the CDN is already doing the work
 * Next's optimizer would duplicate.
 */

type OptimizeImageUrlOptions = {
  /** Longest edge to request, in CSS pixels. */
  size?: number;
  /** Evict the oldest entry past this many, so the cache can't grow unbounded. */
  maxCacheSize?: number;
};

const DEFAULT_SIZE = 400;

// Memoised so a re-render doesn't rebuild the same string for every tile.
const imageUrlCache = new Map<string, string>();

/**
 * Thumbnail size for grid tiles. Smaller on phones, where the tiles are smaller
 * and the connection is likelier to be metered.
 */
export const getOptimalImageSize = (): number => {
  if (typeof window === 'undefined') return DEFAULT_SIZE;
  return window.innerWidth < 768 ? 300 : DEFAULT_SIZE;
};

let tileImageSize: number | null = null;

/**
 * The thumbnail size, resolved once per session.
 *
 * Pinned rather than recomputed so every caller asks the CDN for the *same*
 * URL. That is what makes the preview dialog's placeholder paint instantly: it
 * is a cache hit on the exact image the grid already downloaded. Recomputing
 * across a 768px resize would mint a different URL and cost a fresh request at
 * the moment the dialog opens.
 */
export const getTileImageSize = (): number => {
  if (tileImageSize === null) tileImageSize = getOptimalImageSize();
  return tileImageSize;
};

/** Upper bound on the full-screen request. */
const MAX_LIGHTBOX_SIZE = 1440;

/**
 * Full-screen (preview dialog) size: the CSS viewport width, capped.
 *
 * Deliberately CSS-pixel based with no `devicePixelRatio` multiplier — the
 * sibling repo found that multiplying made phones request a retina-inflated
 * size, while plain width keeps them near device width for a large bandwidth
 * saving. The CDN only downsizes, so asking for more than the source has costs
 * nothing but returns nothing either.
 */
export const getLightboxImageSize = (): number => {
  if (typeof window === 'undefined') return MAX_LIGHTBOX_SIZE;
  return Math.min(window.innerWidth, MAX_LIGHTBOX_SIZE);
};

/**
 * Append the CDN's resize/re-encode params to an asset URL.
 *
 * Returns the input untouched when falsy, so callers can pass an optional
 * poster/thumbnail straight through.
 */
export const optimizeImageUrl = (url: string | undefined, options: OptimizeImageUrlOptions = {}): string | undefined => {
  if (!url) return url;

  const { size = DEFAULT_SIZE, maxCacheSize = 500 } = options;
  const cacheKey = `${url}:${size}`;

  const cached = imageUrlCache.get(cacheKey);
  if (cached) return cached;

  const separator = url.includes('?') ? '&' : '?';
  const optimized = `${url}${separator}type=webp&to=min&r=${size}`;

  if (imageUrlCache.size >= maxCacheSize) {
    const oldestKey = imageUrlCache.keys().next().value;
    if (oldestKey) imageUrlCache.delete(oldestKey);
  }
  imageUrlCache.set(cacheKey, optimized);

  return optimized;
};

export default optimizeImageUrl;
